import React, { lazy, Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { useAuthStore } from '@rfacil/shared/services';
import LoginForm from './components/LoginForm';
import OlvideContrasena from './components/OlvideContrasena';
import RestablecerPassword from './components/RestablecerPassword';
import CambioPassword from './components/CambioPassword';
import AuthRedirect from './components/AuthRedirect';

import { PermissionRouteGuard } from './components/PermissionRouteGuard';
import { MaterialidadRouteGuard } from './components/MaterialidadRouteGuard';
import { CustomSnackbar, Loader, InactivityDialog } from '@rfacil/shared/ui';
import { InstallPrompt } from './components/InstallPrompt';
import { AvisoPendientesBuzon } from './components/AvisoPendientesBuzon';
import { GestionSesion } from '@rfacil/shared/services';
import { consumePendingFacturaLinkPath } from './utils/pending-factura-link';
import { useRefrescoPermisos } from './hooks/useRefrescoPermisos';
const GestionCorporativos = lazy(() => import('./pages/GestionCorporativo'));
const GestionEmpresa = lazy(() => import('./pages/GestionEmpresa'));
const GestionUsuarios = lazy(() => import('./pages/GestionUsuario'));
const GestionRoles = lazy(() => import('./pages/GestionRol'));
const GestionTipoDocumento= lazy(() => import('./pages/GestionTipoDocumento'));
const GestionTipoProveedor = lazy(() => import('./pages/GestionTipoProveedor'));
const GestionRelacionProveedorUsuario = lazy(() => import('./pages/GestionRelacionProvUser'));
const GestionRelacionFacturaManual = lazy(() => import('./pages/GestionRelacionFacturaManual'));
const ConciliacionSat = lazy(() => import('./pages/ConciliacionSat'));
const SolicitudesReasignacion = lazy(() => import('./pages/SolicitudesReasignacion'));
const GestionParametrosEmpresa = lazy(() => import('./pages/GestionParametrosEmpresa'));
const AdministrarAlmacenamiento = lazy(() => import('./pages/AdministrarAlmacenamiento'));
const Auditoria = lazy(() => import('./pages/Auditoria'));
const DetalleAuditoria = lazy(() => import('./pages/DetalleAuditoria'));
const Buzon = lazy(() => import('./pages/Buzon'));
const PreferenciasBuzon = lazy(() => import('./pages/PreferenciasBuzon'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MaterialidadApp = lazy(() => import('../materialidad/app/app'));
const EnlaceFactura = lazy(() => import('./pages/EnlaceFactura'));
/**
 * @component ProtectedRoute
 * @description Un componente que envuelve las rutas para protegerlas del acceso no autenticado.
 * Comprueba si el usuario está autenticado y muestra un spinner de carga mientras comprueba.
 * Si el usuario no está autenticado, lo redirige a la página de inicio de sesión.
 * @param {object} props Las propiedades del componente.
 * @param {React.ReactNode} props.children Los componentes secundarios a renderizar si el usuario está autenticado.
 * @returns {React.ReactElement} El componente renderizado.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

/**
 * @component AppContent
 * @description El componente principal que define las rutas de la aplicación.
 * Utiliza `react-router` (v7) para manejar la navegación y renderiza diferentes componentes según la URL.
 * También utiliza el componente `ProtectedRoute` para proteger las rutas que requieren autenticación.
 * @returns {React.ReactElement} El componente renderizado.
 */
const AppContent: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const pendingPasswordChange = useAuthStore((s) => s.pendingPasswordChange);
  const showVoluntaryPasswordChange = useAuthStore(
    (s) => s.showVoluntaryPasswordChange
  );
  // Un cambio de rol o de actividades llega sin volver a iniciar sesión (al abrir, al cambiar de
  // pantalla y al volver a la pestaña).
  useRefrescoPermisos();

  return (
    <>
      <GestionSesion />
      <InactivityDialog />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route
            path="/login"
            element={
              <AuthRedirect resolveTo={consumePendingFacturaLinkPath}>
                <LoginForm />
              </AuthRedirect>
            }
          />
          <Route
            path="/olvide-contrasena"
            element={
              <AuthRedirect>
                <OlvideContrasena />
              </AuthRedirect>
            }
          />
          <Route
            path="/restablecer-contrasena"
            element={
              <AuthRedirect>
                <RestablecerPassword />
              </AuthRedirect>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Fuera de ProtectedRoute/MaterialidadRouteGuard: la pagina guarda el enlace y manda a /login. */}
          <Route path="/facturas/:token" element={<EnlaceFactura />} />
          <Route
            path="/gestionar-rol"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Roles" allowedRoles={['Empresa']}>
                  <GestionRoles />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
            <Route
              path="/gestionar-relacion-factura-manual"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="RelacionFacturaManual" allowedRoles={['Empresa']}>
                    <GestionRelacionFacturaManual />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/conciliacion-sat"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="ConciliacionSat" allowedRoles={['Empresa']}>
                    <ConciliacionSat />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/solicitudes-reasignacion"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="SolicitudesReasignacion" allowedRoles={['Empresa']}>
                    <SolicitudesReasignacion />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gestionar-tipo-documento"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="GestionarTipoDocumento" allowedRoles={['Operativo']}>
                    <GestionTipoDocumento />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gestionar-tipo-proveedor"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="TipoProveedor" allowedRoles={['Operativo']}>
                    <GestionTipoProveedor />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
              <Route
              path="/gestionar-relacion-proveedor-usuario"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="RelacionUsuarioProveedor" allowedRoles={['Operativo']}>
                    <GestionRelacionProveedorUsuario />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />

          <Route
            path="/gestionar-corporativo"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Corporativos" allowedRoles={['Super']}>
                  <GestionCorporativos />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/administrar-almacenamiento"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Corporativos" allowedRoles={['Super']}>
                  <AdministrarAlmacenamiento />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-empresas"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Empresas" allowedRoles={['Corporativo']}>
                  <GestionEmpresa />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-parametros-empresa"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="EFirmaEmpresa" allowedRoles={['Empresa']}>
                  <GestionParametrosEmpresa />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-usuarios"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Usuarios" allowedRoles={['Super', 'Corporativo', 'Empresa']}>
                  <GestionUsuarios />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/materialidad/*"
            element={
              <ProtectedRoute>
                <MaterialidadRouteGuard>
                  <MaterialidadApp />
                </MaterialidadRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auditoria"
            element={
              <ProtectedRoute>
                {/* Sin `allowedRoles`: el guard combina sección Y rol con AND, así que
                    restringirlo a Empresa expulsaría al Operativo con permiso de bitácora.
                    `canViewAuditoria` ya resuelve ambas audiencias. */}
                <PermissionRouteGuard requiredSection="Auditoria">
                  <Auditoria />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/detalle-auditoria"
            element={
              <ProtectedRoute>
                {/* Mismos guardas que `/auditoria`: el detalle no puede ser más
                    permisivo que el listado del que se abre. */}
                <PermissionRouteGuard requiredSection="Auditoria">
                  <DetalleAuditoria />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buzon"
            element={
              <ProtectedRoute>
                {/* `canViewBuzon` ya resuelve los roles con buzón (Operativo y Empresa). */}
                <PermissionRouteGuard requiredSection="Buzon">
                  <Buzon />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/buzon/preferencias"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Buzon">
                  <PreferenciasBuzon />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CustomSnackbar />
      <InstallPrompt />
      {/* Fuera de las rutas: el aviso de pendientes sale una vez por sesión, en cualquier pantalla. */}
      {isAuthenticated ? <AvisoPendientesBuzon /> : null}
      {isAuthenticated &&
      (pendingPasswordChange || showVoluntaryPasswordChange) ? (
        <CambioPassword />
      ) : null}
    </>
  );
};

/**
 * @function App
 * @description El componente raíz de la aplicación.
 * Envuelve el componente `AppContent` con el `AuthProvider` para proporcionar el contexto de autenticación a todos los componentes secundarios.
 * @returns {React.ReactElement} El componente renderizado.
 */
export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
